/**
 * THE WHEEL PICKER — a value chosen by scrolling a column that snaps.
 *
 * For the setting somebody adjusts a few times a year and should never have to type. Hunsy's is
 * the quiet window: two ends, 48 answers each, and it used to be two `HH:MM` text fields — which
 * asks a person to know the format, get the colon right, and re-derive "so that is eleven hours"
 * in their head every time.
 *
 * `ScrollView` with `snapToInterval` rather than a picker library or a platform picker: the
 * platform ones disagree with each other (iOS has a wheel, Android a clock dial, the web a text
 * input) and none of them is the gesture this control is about. One implementation, the same
 * gesture on all three.
 *
 * It commits on DONE only. A wheel that wrote through on every scroll would save four times on the
 * way past the value somebody wanted, and Cancel would have nothing to restore.
 *
 * A wheel is wrong for a value needing arbitrary precision. Steps are the setting's own — the
 * caller supplies the list, and a stored value not on the list opens at the nearest step.
 */
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import * as React from 'react';
import {
  Pressable,
  ScrollView,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';

/** One row's height. The whole geometry is measured off it. */
const ROW_H = 36;
/** Odd, so there is a true centre row for the band to sit on. */
const VISIBLE_ROWS = 5;
const COLUMN_H = ROW_H * VISIBLE_ROWS;
const PAD = (COLUMN_H - ROW_H) / 2;

/** One column: what it is called, what it holds, and what it is on. */
export interface WheelColumn {
  /** The tracked caption over it, and the screen reader's name for the column. */
  label: string;
  /** The value in force. Not required to be in `values` — the wheel opens at the nearest one. */
  value: string;
  /** Every value the column offers, in order. */
  values: readonly string[];
  onChange: (value: string) => void;
  /**
   * Where a value NOT in `values` sits, for a column whose steps are coarser than what the API
   * accepts. Hunsy's quiet window is stored to the minute and offered in half-hours, so an
   * off-grid `22:07` opens on `22:00` rather than at the top of the list. Without it an unknown
   * value opens at index 0, which is honest but not helpful.
   */
  nearest?: (value: string) => number;
  /**
   * How a value READS, where the stored form is not the readable one.
   *
   * The quiet window stores and shows `22:00`, so it needs none of this. A day wheel stores
   * `2026-09-03` — the shape every day column in this app carries — and must never show it: an
   * ISO date in a list somebody is scrolling through is a database column read out loud. The
   * label is used for the rows, for the pressed value and for what a screen reader announces, so
   * there is no path by which the raw form reaches anybody.
   */
  format?: (value: string) => string;
}

export function WheelPicker({
  columns,
  readout,
  onCancel,
  onDone,
  className,
}: {
  /** One or two. Three columns in a phone's width is three unreadable columns. */
  columns: readonly WheelColumn[];
  /** The live sentence under them, derived by the caller from the drafts it holds. */
  readout: string;
  onCancel: () => void;
  onDone: () => void;
  className?: string;
}) {
  return (
    <View className={cn('border-border gap-3 rounded-lg border p-3', className)}>
      <View className="flex-row gap-3">
        {columns.map((column) => (
          <Column key={column.label} column={column} />
        ))}
      </View>

      {/* The readout, not a second label per column: "22:00 → 09:00" is two facts and "11 h of
          quiet" is the one anybody is actually deciding. */}
      <Text variant="small" className="text-muted-foreground text-center">
        {readout}
      </Text>

      <View className="flex-row flex-wrap justify-end gap-2">
        <Button variant="ghost" flush={false} onPress={onCancel}>
          <Text>Cancel</Text>
        </Button>
        <Button onPress={onDone}>
          <Text>Done</Text>
        </Button>
      </View>
    </View>
  );
}

/**
 * One column.
 *
 * The centred row is the value, and it is arrived at three ways because none of them is reliable
 * everywhere: `onMomentumScrollEnd` (which react-native-web does not always fire), a debounced
 * `onScroll` underneath it, and a plain press on any row.
 *
 * ACCESSIBILITY is the fourth way, and the only one that does not need a pointer. The column is
 * one `adjustable` node with increment and decrement actions, so a screen reader reads "From,
 * 22:00" and moves it a step at a time — rather than swiping through forty-eight buttons to find
 * one. `accessible` on the wrapper is what collapses those forty-eight into one node; the row
 * presses stay for fingers and mice, which is what they were always for.
 */
function Column({ column }: { column: WheelColumn }) {
  const { label, value, values, onChange, nearest, format } = column;
  const show = format ?? ((v: string) => v);
  const ref = React.useRef<ScrollView>(null);
  const debounce = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const placed = React.useRef(false);

  React.useEffect(() => () => {
    if (debounce.current) clearTimeout(debounce.current);
  }, []);

  /** The nearest step to whatever the column is on, which need not be one of the steps. */
  const index = React.useMemo(() => {
    const exact = values.indexOf(value);
    if (exact >= 0) return exact;
    const guess = nearest?.(value) ?? 0;
    return Math.min(values.length - 1, Math.max(0, guess));
  }, [values, value, nearest]);

  const commit = (y: number) => {
    const i = Math.min(values.length - 1, Math.max(0, Math.round(y / ROW_H)));
    const next = values[i];
    if (next && next !== value) onChange(next);
  };

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => commit(y), 120);
  };

  /** One step either way, and never past the ends: a wheel of times does not wrap. */
  const step = (by: number) => {
    const i = Math.min(values.length - 1, Math.max(0, index + by));
    const next = values[i];
    if (!next || next === value) return;
    onChange(next);
    ref.current?.scrollTo({ y: i * ROW_H, animated: true });
  };

  return (
    <View
      accessible
      accessibilityRole="adjustable"
      accessibilityLabel={label}
      // BOTH forms, and they are not redundant: react-native-web reads `aria-valuetext` and drops
      // the `accessibilityValue` object entirely, so without this a column announced as a slider
      // with no value at all — which is worse than the forty-eight buttons it replaced. React
      // Native takes the object, and takes `aria-valuetext` as an alias, so the two agree.
      accessibilityValue={{ text: show(value) }}
      aria-valuetext={show(value)}
      accessibilityActions={ADJUST_ACTIONS}
      onAccessibilityAction={(e) => step(e.nativeEvent.actionName === 'decrement' ? -1 : 1)}
      className="min-w-0 flex-1 gap-1">
      {/* Centred, because the values under it are: a left-aligned head over a centred column
          reads as a label for the column to its left, which is what the readiness capture showed
          FROM and UNTIL doing. */}
      <Text variant="caption" className="text-muted-foreground text-center">
        {label}
      </Text>

      <View style={{ height: COLUMN_H }} className="overflow-hidden">
        {/* The band the value sits in. First in the tree so the rows paint over it, and inert so
            the gesture belongs to the scroll view underneath the finger. */}
        <View
          pointerEvents="none"
          className="bg-accent border-border absolute left-0 right-0 border-y"
          style={{ top: PAD, height: ROW_H }}
        />

        <ScrollView
          ref={ref}
          showsVerticalScrollIndicator={false}
          snapToInterval={ROW_H}
          decelerationRate="fast"
          nestedScrollEnabled
          scrollEventThrottle={16}
          onScroll={onScroll}
          onMomentumScrollEnd={(e) => commit(e.nativeEvent.contentOffset.y)}
          onScrollEndDrag={(e) => commit(e.nativeEvent.contentOffset.y)}
          // Padded top and bottom so the first and last values can sit in the centre band.
          contentContainerStyle={{ paddingVertical: PAD }}
          // Placed once, on the first layout: the wheel opens on the value it was given rather
          // than at the top. Later scrolls are the person's and must not be overridden.
          onLayout={() => {
            if (placed.current) return;
            placed.current = true;
            ref.current?.scrollTo({ y: index * ROW_H, animated: false });
          }}>
          {values.map((v, i) => (
            <Pressable
              key={v}
              onPress={() => {
                onChange(v);
                ref.current?.scrollTo({ y: i * ROW_H, animated: true });
              }}
              style={{ height: ROW_H }}
              className="items-center justify-center">
              <Text variant="small" className={v === value ? 'font-semibold' : 'text-muted-foreground'}>
                {show(v)}
              </Text>
            </Pressable>
          ))}
        </ScrollView>
      </View>
    </View>
  );
}

/** Hoisted: the same array on every render, since it never varies. */
const ADJUST_ACTIONS = [{ name: 'increment' }, { name: 'decrement' }] as const;
